import React from 'react'

interface StudentSectionProps {
  title: string
  description?: string
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  actions?: React.ReactNode
  children: React.ReactNode
  bleed?: boolean
  className?: string
  padding?: 'default' | 'compact' | 'none'
  variant?: 'default' | 'outlined' | 'elevated' | 'gradient'
}

export const StudentSection: React.FC<StudentSectionProps> = ({
  title,
  description,
  icon: Icon,
  actions,
  children,
  bleed = false,
  className = '',
  padding = 'default',
  variant = 'default'
}) => {
  const paddingClass = {
    none: '',
    compact: 'p-4 sm:p-5',
    default: 'p-6 sm:p-8'
  }[padding]

  const variantClass = {
    default: 'bg-white rounded-3xl shadow-sm border border-slate-100',
    outlined: 'bg-transparent rounded-3xl border-2 border-slate-200 border-dashed',
    elevated: 'bg-white rounded-3xl shadow-lg shadow-slate-200/50 border border-slate-100',
    gradient:
      'bg-gradient-to-br from-white via-white to-indigo-50/30 rounded-3xl shadow-sm border border-slate-100'
  }[variant]

  const baseClass = bleed ? '' : variantClass

  return (
    <section className={`${baseClass} ${paddingClass} ${className} transition-all duration-200`}>
      {/* 头部 */}
      <div className="flex flex-col gap-3 mb-6 sm:mb-8 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center shadow-sm">
              <Icon className="w-6 h-6 text-indigo-600" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
            {description && (
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed max-w-2xl">{description}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">{actions}</div>
        )}
      </div>

      {/* 内容 */}
      {children}
    </section>
  )
}

// 快捷章节标题组件
export const SectionTitle: React.FC<{
  title: string
  description?: string
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  size?: 'sm' | 'md' | 'lg'
}> = ({ title, description, icon: Icon, size = 'md' }) => {
  const titleSizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl'
  }

  const iconSizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  }

  return (
    <div className="flex items-center gap-3 mb-4">
      {Icon && (
        <div
          className={`${iconSizes[size]} rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center`}
        >
          <Icon className="w-1/2 h-1/2 text-indigo-600" />
        </div>
      )}
      <div>
        <h3 className={`${titleSizes[size]} font-bold text-slate-900`}>{title}</h3>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
  )
}

// 章节分隔符组件
export const SectionDivider: React.FC<{ text?: string }> = ({ text }) => {
  if (text) {
    return (
      <div className="flex items-center gap-4 my-8">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{text}</span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>
    )
  }

  return (
    <div className="my-8 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
  )
}

export default StudentSection
